<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { type Feedback } from '@/shared/apollo-api/__generated__/types';
import FeedbackItem from '@/features/product-page/ui/mobile/feedback-item.vue';
import { useFeedback } from '@/features/product-page/business/useFeedback';
import ProductTabsReviewsPhotoSlider from '../common/tabs-reviews/product-tabs-reviews-photo-slider.vue';
import { Plural } from '@/shared/helpers';
import { IconChevronRightBold } from '@/features/ui-kit/icons';
import { useOtherReviewPopup } from '../../features';

// import { PageType } from '@mm-frontend/frontend-bunseki';
// import { clickstreamTracker } from '@/shared/clickstream';
// import { EUiPropertiesAction, EUiPropertiesSpaceType } from '@mm-frontend/clickstream-client-js';
// import { openReviewsPopup } from '../../business/reviews-popup';
import { useProductModals } from '../../business/useProductModals';
import { MMText } from '@mm-frontend/mm-web-ui-kit';

interface Props {
  productId: number;
  reviewsAmount: number;
}

const props = defineProps<Props>();

const {
  feedback,
  feedbackWithPhotos,
  feedbackLoading,
  feedbackWithPhotosLoading,
  hasMoreFeedback,
  hasMoreFeedbackWithPhotos,
  feedbackQuantity,
  externalRating,
  rating,
  loadMoreFeedback,
  loadMoreFeedbackWithPhotos,
  initializeFeedback,
} = useFeedback();

const { openFeedbackModal } = useProductModals();

const feedbackQuantityForTabs = computed(() => {
  return externalRating.value ? 0 : props.reviewsAmount;
});

// Форматирование количества отзывов с правильным склонением
const formattedReviewsText = computed(() => {
  return Plural.with(feedbackQuantityForTabs.value, 'отзыв|отзыва|отзывов');
});

const { showOtherReviewPopup } = useOtherReviewPopup();
function openFullCharacteristics() {
  if (externalRating.value) {
    showOtherReviewPopup({
      rating: externalRating.value,
      reviews: props.reviewsAmount,
    });
  } else {
    openFeedbackModal(props.productId, feedbackQuantityForTabs.value, rating.value);
  }
}

function eventShow(feed: Feedback) {
  console.log('Analytics: FEEDBACK_IMPRESSION', {
    space_name: 'Product',
    space_type: 'PAGE',
    space_id: String(props.productId),
    action: 'SHOW',
    product_id: props.productId,
    feedback_id: feed.id,
    rating: feed.rating,
    is_edited: false,
    is_anonymous: feed.anonymous,
    has_text: Boolean(feed.content),
    has_seller_response: Boolean(feed.reply),
    has_media: Boolean(feed.photos),
    page_type: 'PRODUCT',
  });
}

onMounted(async () => {
  // Initialize feedback data
  await initializeFeedback();
});
</script>

<template>
  <div id="mobile-feedback" class="feedback">
    <div class="header-wrap">
      <MMText font="Title L/Semibold" tag="span" data-test-id="text__feedback-title">
        {{ formattedReviewsText }}
      </MMText>
      <MMText
        font="Body M/Medium"
        data-test-id="button__feedback-all"
        @click="openFullCharacteristics()"
        tag="span"
        style="display: flex; align-items: center"
      >
        Все&nbsp;
        <IconChevronRightBold :size="16" />
      </MMText>
    </div>
    <product-tabs-reviews-photo-slider
      v-if="feedbackWithPhotos.length"
      style="margin: 25px 0"
      :feedbackWithPhotos="feedbackWithPhotos"
      :feedbackWithPhotosLoading="feedbackWithPhotosLoading"
      @loadMoreFeedbackWithPhotos="loadMoreFeedbackWithPhotos"
    />
    <div class="feedback-wrap" data-test-id="list__list-feedback-item">
      <div
        v-for="feed in feedback"
        :key="feed.id"
        class="feedback-item"
        data-test-id="item__feedback-item"
      >
        <feedback-item :feedback="feed" @event-show-review="eventShow(feed)" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="stylus">
.feedback
  margin-top 32px
  scroll-margin-top: 98px;

.header-wrap
  display flex
  justify-content space-between

  svg
    fill var(--light-text-tertiary, #8B8E99);

.feedback-wrap
  display flex
  gap 12px
  overflow-x scroll
  margin: 12px -16px 0px -16px;
  padding: 0 16px;
  scroll-snap-type: x mandatory;
  scroll-padding-left: 16px;

.feedback-item
  border-radius: 16px;
  border: 1px solid var(--ui-light-border-emphasis-on-primary, #D0D2D9);
  background: var(--ui-light-surface-primary-rest, #FFF);
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1 0 auto;
  width 100%
  max-width 252px
  max-height 242px
  scroll-snap-align: start;

  .content
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    max-height 100%
    max-width  210px
    text-overflow ellipsis
    overflow hidden
    &-text
      margin 0 auto
      color: var(--ui-light-text-primary, #1F1F26);
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 120% */
</style>
